import React from "react";
import ReactDOM from "react-dom/client";
import {
  createRouter,
  RouterProvider,
  createRootRoute,
  createRoute,
  Outlet,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { UploadDropzone } from "./components/UploadDropzone.js";
import { ChatView } from "./components/ChatView.js";
import {
  listRecentSessions,
  type SessionSummary,
} from "./lib/api.js";
import { useAuth } from "./auth/useAuth.js";

const rootRoute = createRootRoute({ component: Root });

function Root() {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return <div className="page dim">Loading…</div>;
  }
  if (!isAuthenticated) {
    return <LoginPage />;
  }
  return <Outlet />;
}

function LoginPage() {
  const { signInWithEmail, signInWithGoogle, signInWithGitHub } = useAuth();
  const [email, setEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isValid = useMemo(() => email.includes("@") && email.length > 3, [email]);

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!isValid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await signInWithEmail(email);
      if (res?.error) {
        setError(res.error.message ?? "failed to send magic link");
      } else {
        setLinkSent(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function social(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="page auth-page">
      <header>
        <h1>EXEGESIS</h1>
        <p className="dim">Private Beta</p>
      </header>
      {linkSent ? (
        <div className="auth-card">
          <p>
            <strong>Check your inbox.</strong> We sent a sign-in link to{" "}
            <em>{email}</em>.
          </p>
          <p className="dim">
            Click the link to sign in. The link expires in 5 minutes.
          </p>
          <button
            className="link-button"
            onClick={() => {
              setLinkSent(false);
              setEmail("");
            }}
          >
            Use a different email
          </button>
        </div>
      ) : (
        <div className="auth-card">
          <button
            type="button"
            className="oauth-btn"
            onClick={() => social(signInWithGoogle)}
            disabled={busy}
          >
            Continue with Google
          </button>
          <button
            type="button"
            className="oauth-btn"
            onClick={() => social(signInWithGitHub)}
            disabled={busy}
          >
            Continue with GitHub
          </button>

          <div className="auth-divider"><span>or</span></div>

          <form onSubmit={submit}>
            <label>
              Email
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="you@example.com"
              />
            </label>
            {error && <div className="error">{error}</div>}
            <button disabled={busy || !isValid}>
              {busy ? "Sending…" : "Send magic link"}
            </button>
            <p className="dim" style={{ fontSize: "12px", marginTop: 12 }}>
              We'll email you a magic link — no password needed.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Index,
});

function Index() {
  const nav = useNavigate();
  const { signOut } = useAuth();
  const [recent, setRecent] = useState<SessionSummary[]>([]);

  useEffect(() => {
    listRecentSessions().then(setRecent).catch(() => {});
  }, []);

  return (
    <div className="page">
      <header>
        <div>
          <h1>EXEGESIS</h1>
          <p className="dim">Agentic Reverse Engineering</p>
        </div>
        <button
          className="link-button"
          onClick={() => signOut().then(() => window.location.reload())}
        >
          Sign out
        </button>
      </header>
      <UploadDropzone
        onUploaded={(id) =>
          nav({ to: "/chat/$sessionId", params: { sessionId: id } })
        }
      />
      {recent.length > 0 && (
        <div className="recent-sessions">
          <div className="recent-header">Recent sessions</div>
          {recent.map((s) => {
            const name = s.binaryPath.split("/").pop() || s.sessionId.slice(0, 8);
            const when = formatRelativeTime(s.lastActivity);
            return (
              <button
                key={s.sessionId}
                className="recent-item"
                onClick={() =>
                  nav({
                    to: "/chat/$sessionId",
                    params: { sessionId: s.sessionId },
                  })
                }
              >
                <div className="recent-name">{name}</div>
                <div className="dim recent-meta">
                  {s.sessionId.slice(0, 8)} · {when}
                  {s.hasActivity ? " · has history" : ""}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatRelativeTime(ts: number): string {
  const delta = Date.now() - ts;
  if (delta < 60_000) return "just now";
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return `${Math.floor(delta / 86_400_000)}d ago`;
}

const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chat/$sessionId",
  component: ChatPage,
});

function ChatPage() {
  const { sessionId } = useParams({ from: "/chat/$sessionId" }) as {
    sessionId: string;
  };
  return <ChatView sessionId={sessionId} />;
}

const routeTree = rootRoute.addChildren([indexRoute, chatRoute]);
const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);
